import React from 'react';
import './ShiftInclusions.scss';

/** Short visible labels for chips / icon+label UI */
const shortLabel = (name, included) => (
  included ? `${name} included` : `${name} not included`
);

/** Hover / screen-reader wording (clearer about costs) */
const tooltipLabel = (name, included) => (
  included ? `${name} costs included` : `${name} costs not included`
);

/** Full prose for detail accordion sections */
export const getInclusionDetailText = (name, included) => (
  included ? `${name} costs are included` : `${name} costs are not included`
);

const inclusionLabel = (name, included, compact) => {
  if (compact) {
    return shortLabel(name, included);
  }
  return `${name}: ${included ? 'Included' : 'Not included'}`;
};

const TravelIcon = () => (
  <svg className={'inclusion-svg'} viewBox={'0 0 24 24'} aria-hidden={'true'} focusable={'false'}>
    <path
      fill={'currentColor'}
      d={'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z'}
    />
  </svg>
);

const AccommodationIcon = () => (
  <svg className={'inclusion-svg'} viewBox={'0 0 24 24'} aria-hidden={'true'} focusable={'false'}>
    <path
      fill={'currentColor'}
      d={'M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z'}
    />
  </svg>
);

const InclusionIcon = ({ name, included, showLabel }) => {
  const visibleLabel = shortLabel(name, included);
  const accessibleLabel = tooltipLabel(name, included);
  const Icon = name === 'Travel' ? TravelIcon : AccommodationIcon;

  return (
    <span
      className={`inclusion-icon ${included ? 'included' : 'not-included'}${showLabel ? ' with-label' : ''}`}
      title={accessibleLabel}
      aria-label={accessibleLabel}
    >
      <span className={'inclusion-icon-glyph'}>
        <Icon />
      </span>
      {showLabel && <span className={'inclusion-icon-text'}>{visibleLabel}</span>}
    </span>
  );
};

/**
 * Travel/accommodation inclusion indicators.
 * variants:
 * - chips: text pills (default)
 * - icons: icon-only (cards)
 * - iconLabels: icon + short text (detail)
 */
const ShiftInclusions = ({
  travel = false,
  accommodation = false,
  compact = false,
  variant = 'chips',
}) => {
  const travelIncluded = travel === true;
  const accommodationIncluded = accommodation === true;
  const useIcons = variant === 'icons' || variant === 'iconLabels';
  const showLabel = variant === 'iconLabels';

  if (useIcons) {
    return (
      <div
        className={`shift-inclusions icons${variant === 'icons' ? ' icons-only' : ' icon-labels'}`}
        aria-label={'Travel and accommodation'}
      >
        <InclusionIcon name={'Travel'} included={travelIncluded} showLabel={showLabel} />
        <InclusionIcon name={'Accommodation'} included={accommodationIncluded} showLabel={showLabel} />
      </div>
    );
  }

  return (
    <div className={`shift-inclusions${compact ? ' compact' : ''}`} aria-label={'Travel and accommodation'}>
      <span className={`inclusion-chip ${travelIncluded ? 'included' : 'not-included'}`}>
        {inclusionLabel('Travel', travelIncluded, compact)}
      </span>
      <span className={`inclusion-chip ${accommodationIncluded ? 'included' : 'not-included'}`}>
        {inclusionLabel('Accommodation', accommodationIncluded, compact)}
      </span>
    </div>
  );
};

export default ShiftInclusions;
